import { ExpandMore, ExitToApp, PersonOutline, SettingsOutlined } from '@mui/icons-material';
import { Navbar as FinaraNavbar, type NavItem } from 'finara-components';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationDropdown } from '@/components/notifications';
import { useAuth } from '@/hooks/useAuth';

interface NavbarProps {
  navItems: NavItem[];
  userName?: string;
  activeItem?: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  navItems,
  userName = 'Admin User',
  activeItem,
}) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const displayName = user?.name ?? userName;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    void logout();
  };

  const handleNavigate = (path: string) => {
    setIsDropdownOpen(false);
    void navigate(path);
  };

  // Right side navbar content
  const rightContent = (
    <div className="flex items-center space-x-3">
      <NotificationDropdown />

      {/* User Profile Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center space-x-2 cursor-pointer px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-navbar-text hover:text-navbar-hover"
        >
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #A6E77F 0%, #175757 100%)' }}
          >
            <span className="text-white text-[10.5px] font-medium select-none">
              {displayName
                .split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </span>
          </div>
          <span className="text-sm font-medium">{displayName}</span>
          <ExpandMore fontSize="small" />
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-surface rounded-lg shadow-lg border border-border py-2 z-50">
            <button
              onClick={() => handleNavigate('/perfil')}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-white hover:bg-white/5"
            >
              <PersonOutline fontSize="small" className="text-muted" />
              Perfil
            </button>
            <button
              onClick={() => handleNavigate('/configuracion')}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-white hover:bg-white/5"
            >
              <SettingsOutlined fontSize="small" className="text-muted" />
              Configuración
            </button>
            <div className="border-t border-border my-1"></div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10"
            >
              <ExitToApp fontSize="small" />
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>
    </div>
  );
  if (activeItem) {
    navItems = navItems.map(item => ({
      ...item,
      active:
        'text' in item
          ? item.text === activeItem && (item.active = true)
          : item.title === activeItem && (item.active = true),
    }));
  }

  // Append logout as a nav item so it appears in the mobile hamburger menu
  const navItemsWithLogout = [
    ...navItems,
    { text: 'Cerrar sesión', url: '#logout', icon: <ExitToApp fontSize="small" /> },
  ];

  return (
    <FinaraNavbar
      logoAlt="Finara"
      logoUrl="/"
      navItems={navItemsWithLogout}
      rightContent={rightContent}
      onNavLinkClick={item => {
        if (item.url === '#logout') {
          handleLogout();
        }
      }}
      onLogoClick={() => {
        /* Handle logo click */
      }}
    />
  );
};
